import { useCallback, useMemo } from "react";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { useTagOptions } from "@/api/services/main/tag";
import { useJobFilterOptions } from "@/api/services/main/job";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { truncateWithEllipsis } from "@/utils/string";
import { TOption } from "@/types/common";
import { FILTER_LABEL_MAX_LENGTH, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH, JOB_FILTER_COMPANY_ARCHIVED } from "@/features/job/components/job-list/job-filter-shared";

export const useJobFilterMasterData = () => {
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { data: jobStatusOptions } = useEnum("EJobStatus");
  const { data: jobTagOptions } = useTagOptions("job");
  const { data: recruitmentLocationTypeOptions } = useEnum("ERecruitmentLocationType");
  const { data: japaneseLevelTypeOptions } = useEnum("EJapaneseLevelType");
  const { managementNumberOptions, jobNameOptions, workplaceOptions, companyNameOptions } = useJobFilterOptions();
  const { userOptions: recruiterOptions } = useGetUserListPersonInCharge();

  const truncateOpts = useCallback(
    (options: TOption[], maxLength = FILTER_LABEL_MAX_LENGTH) =>
      options.map((opt) => ({ ...opt, label: truncateWithEllipsis(opt.label, maxLength) })),
    [],
  );


  const statusOptionsWithArchive = useMemo(() => {
    const options = [...(jobStatusOptions ?? [])];
    options.push({ value: JOB_FILTER_COMPANY_ARCHIVED, label: "アーカイブ済み" });
    return options;
  }, [jobStatusOptions]);

  const truncatedOptions = useMemo(
    () => ({
      status: truncateOpts(statusOptionsWithArchive),
      tagIds: truncateOpts(jobTagOptions ?? []),
      managementNumber: truncateOpts(managementNumberOptions, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH),
      companyName: truncateOpts(companyNameOptions),
      jobName: truncateOpts(jobNameOptions),
      recruiter: truncateOpts(recruiterOptions),
      industry: truncateOpts(companySpecificIndustryOptions),
      japaneseLevelType: truncateOpts(japaneseLevelTypeOptions ?? []),
      workplace: truncateOpts(workplaceOptions, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH),
      recruitmentLocationType: truncateOpts(recruitmentLocationTypeOptions ?? []),
    }),
    [
      truncateOpts,
      statusOptionsWithArchive,
      jobTagOptions,
      managementNumberOptions,
      companyNameOptions,
      jobNameOptions,
      recruiterOptions,
      companySpecificIndustryOptions,
      japaneseLevelTypeOptions,
      workplaceOptions,
      recruitmentLocationTypeOptions,
    ],
  );

  const rawOptions = useMemo(
    () => ({
      status: statusOptionsWithArchive,
      tagIds: jobTagOptions ?? [],
      companyName: companyNameOptions ?? [],
      recruiter: recruiterOptions,
      industry: companySpecificIndustryOptions,
      japaneseLevelType: japaneseLevelTypeOptions ?? [],
      recruitmentLocationType: recruitmentLocationTypeOptions ?? [],
    }),
    [statusOptionsWithArchive, jobTagOptions, companyNameOptions, recruiterOptions, companySpecificIndustryOptions, japaneseLevelTypeOptions, recruitmentLocationTypeOptions],
  );

  return { truncatedOptions, rawOptions };
};
